import { app, component, Component, ojs } from "modular-openscriptjs";
import { dom } from "../../fotastart";
import {
	either,
	hasPlaceholderShown,
	uniqueId,
} from "../../helpers";

const h = app("h");

export default class FormField extends Component {
	render(config, ...args) {
		const self = this; // Necessary to reference the component instance inside DOM event callbacks
		const hook = `ff-${config?.id ?? uniqueId()}`;
		// Extract core UI setup properties
		const {
			tag = "input",
			label: labelObj,
			icons,
			helpText: helpTextObj,
			dependencies = [],
			dependencyUpdateEvent = "ff-dependency-update",
			...inputAttrs
		} = config || {};

		// `dependencyUpdateEvent`: The event to fire to the dependent input elements
		// when the input event is fired on this element.
		// To avoid clashing with element's input event,
		// we use a CustomEvent by default.

		const label = labelObj?.value ?? "Label";
		const hasFloatingLabel = labelObj?.floats ?? true;
		const leadingIcon = icons?.leading;
		const trailingIcon = icons?.trailing;
		const hasLeadingIcon = !!leadingIcon;
		const hasTrailingIcon = !!trailingIcon;
		const helpText = helpTextObj?.value;

		// Values that shouldn't be empty:
		// `id` is needed for the label,
		// `type` is needed to set a default,
		// `placeholder` is needed for floating labels (CSS)
		const id = config?.id ?? hook;
		const type = config?.type ?? "text";
		const placeholder = config?.placeholder ?? "...";

		// The input element's value will be its innerHTML for [contenteditable="true"]
		const contenteditable = config?.contenteditable ?? false;
		if (contenteditable || tag === "TextArea") delete inputAttrs.value;

		const externalListeners = inputAttrs.listeners || {};
		delete inputAttrs.listeners;

		const mergedListeners = {
			focus: function (event) {
				dom.get(`[data-ff-wrapper=${hook}]`)?.classList.add("has-focus");
				if (externalListeners.focus) externalListeners.focus.call(this, event);
			},
			blur: function (event) {
				dom.get(`[data-ff-wrapper=${hook}]`)?.classList.remove("has-focus");
				if (externalListeners.blur) externalListeners.blur.call(this, event);
			},
			input: function (event) {
				if (event instanceof CustomEvent) {
					// The signature of the firer of this event.
					// do what you want to do with this information.
					const signature = event.detail.signature;
					console.log(`Artificial 'input' Event`, signature);
				} else {
					self.placeLabelAccordingly(event.target, hook);

					// This event is organic as per our standards
					// we can notify all dependencies of an
					// input change, i.e. fire a CustomEvent
					// on all dependent input fields!

					// We use `data-ff-dependency-hook` as the dependency
					// attribute that's passed to this element
					// through the `inputAttrs`.
					const thisDepHook = event.target.getAttribute("data-ff-dependency-hook");

					for (const depHook of dependencies) {
						// Ensure it's not this element
						if (depHook === thisDepHook) continue;

						const depInput = dom.get(`[data-ff-dependency-hook="${depHook}"]`);
						if (!depInput) continue;

						// Dispatch custom input event
						const e = new CustomEvent(dependencyUpdateEvent, {
							bubbles: true,
							cancelable: true,
							detail: {
								// Hook of this element firing the event
								signature: thisDepHook
							}
						});

						depInput.dispatchEvent(e);
					}
				}
				if (externalListeners.input) externalListeners.input.call(this, event);
			},
			change: function (event) {
				self.placeLabelAccordingly(event.target, hook);
				if (externalListeners.change) externalListeners.change.call(this, event);
			},
			[dependencyUpdateEvent]: function (event) {
				self.placeLabelAccordingly(event.target, hook);
				if (externalListeners[dependencyUpdateEvent]) externalListeners[dependencyUpdateEvent].call(this, event);
			}
		};

		// Add any other external listeners (e.g. click, keyup)
		for (const key in externalListeners) {
			if (!mergedListeners[key]) {
				mergedListeners[key] = externalListeners[key];
			}
		}

		const inputField = h[tag](
			{
				class: [
					tag.toLowerCase() === "select" ? "form-select" : "form-control",
					"form-field-input"
				].join(" ").trim(),
				type: type,
				id: id,
				placeholder: placeholder,
				data_ff_input: hook,
				listeners: mergedListeners,
				...inputAttrs
			},
			...(tag.toLowerCase() === "select" ? args : []) // For <select>, pass options as children
		);

		if (contenteditable || tag === "TextArea") inputField.innerHTML = config?.value;
		const showPlaceholder = hasPlaceholderShown(inputField);

		return h.div(
			{
				class: [
					"form-field-wrapper",
					hasFloatingLabel ? "has-floating-label" : "",
					hasLeadingIcon ? "has-leading-icon" : "",
					hasTrailingIcon ? "has-trailing-icon" : "",
					showPlaceholder ? "has-placeholder-shown" : "",
				]
					.join(" ")
					.trim(),
				data_ff_wrapper: hook
			},
			h.div(
				{ class: "form-field" },
				either(hasLeadingIcon, () =>
					h.span(
						{
							class: "form-field-icon form-field-icon-leading",
							data_ff_icon_leading: hook,
						},
						leadingIcon
					)
				),
				either(hasTrailingIcon, () =>
					h.span(
						{
							class: "form-field-icon form-field-icon-trailing",
							data_ff_icon_trailing: hook,
						},
						trailingIcon
					)
				),
				inputField
			),
			h.label(
				{
					class: [
						"form-field-label",
						hasFloatingLabel ? "form-field-label-floating" : "",
					]
						.join(" ")
						.trim(),
					data_ff_label: hook,
					for: id
				},
				h.span(label)
			),
			either(helpText, () =>
				h.div(
					{ class: "form-field-text", data_ff_text: hook },
					helpText
				)
			),
			...(tag.toLowerCase() === "select" ? [] : args) // For <select>, `args` are already passed to <select>.
		);
	}

	placeLabelAccordingly(input, hook) {
		const wrap = dom.get(`[data-ff-wrapper=${hook}]`);
		wrap.classList.toggle(
			"has-placeholder-shown",
			hasPlaceholderShown(input)
		);
		if (typeof input.checkValidity === "function") {
			wrap.classList.toggle("is-invalid", !input.checkValidity());
		}
	}
}

ojs(FormField);
